import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { projectsApi } from '@/api/projects'
import { apiErrorMessage } from '@/lib/errors'
import type { Project } from '@/types/models'

export const useProjectsStore = defineStore('projects', () => {
  const projects = ref<Project[]>([])
  const initialized = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // listProjects only fetches the first page (see src/api/projects.ts), so
  // search can silently miss matches beyond it. Tracked so the UI can warn
  // the user rather than implying the search is exhaustive.
  const hasMoreProjects = ref(false)

  // Persisted so a page reload or a direct nav to /history doesn't lose the
  // selection and bounce back to the project picker.
  const ACTIVE_PROJECT_STORAGE_KEY = 'voice.activeProjectId'

  // The Record screen (next ticket) reads this to know which project a memo
  // is about, without the user re-picking it every navigation.
  const activeProjectId = ref<string | null>(localStorage.getItem(ACTIVE_PROJECT_STORAGE_KEY))

  const activeProject = computed(
    () => projects.value.find((project) => project.id === activeProjectId.value) ?? null,
  )

  const searchQuery = ref('')

  function setSearchQuery(query: string): void {
    searchQuery.value = query
  }

  const filteredProjects = computed(() => {
    const query = searchQuery.value.trim().toLowerCase()
    if (!query) return projects.value
    return projects.value.filter((project) => project.name.toLowerCase().includes(query))
  })

  let requestVersion = 0

  async function fetchProjects(): Promise<void> {
    const thisRequestVersion = ++requestVersion
    loading.value = true
    error.value = null
    try {
      const response = await projectsApi.listProjects()
      if (thisRequestVersion === requestVersion) {
        // Copied rather than assigned by reference: updateLinearAutoRun below
        // replaces individual array slots in place, and this array may be
        // shared/cached by the caller (e.g. reused response fixtures in tests).
        projects.value = [...response.data]
        hasMoreProjects.value = response.meta.last_page > 1
      }
    } catch (e: unknown) {
      if (thisRequestVersion === requestVersion) {
        projects.value = []
        hasMoreProjects.value = false
        error.value = apiErrorMessage(e, 'Failed to load projects.')
      }
    } finally {
      if (thisRequestVersion === requestVersion) {
        initialized.value = true
        loading.value = false
      }
    }
  }

  function selectProject(id: string): void {
    if (!projects.value.some((p) => p.id === id)) return
    activeProjectId.value = id
    localStorage.setItem(ACTIVE_PROJECT_STORAGE_KEY, id)
  }

  // Optimistic: flips the local flag immediately so the toggle feels
  // instant, then rolls back and rethrows (with a friendly message) if the
  // API rejects it, so the caller can show an inline error next to the row.
  // Replaces the array entry rather than mutating the fetched project object
  // in place, since that object may be shared/cached elsewhere.
  async function updateLinearAutoRun(id: string, enabled: boolean): Promise<void> {
    const index = projects.value.findIndex((p) => p.id === id)
    if (index === -1) return

    const previous = projects.value[index]
    projects.value[index] = { ...previous, linear_auto_run_enabled: enabled }
    try {
      const updated = await projectsApi.updateProject(id, { linear_auto_run_enabled: enabled })
      const currentIndex = projects.value.findIndex((p) => p.id === id)
      if (currentIndex !== -1) projects.value[currentIndex] = updated
    } catch (e: unknown) {
      const currentIndex = projects.value.findIndex((p) => p.id === id)
      if (currentIndex !== -1) projects.value[currentIndex] = previous
      throw new Error(apiErrorMessage(e, 'Failed to update Linear auto-run.'))
    }
  }

  // Called on logout so the next session's picker doesn't briefly show the
  // previous user's projects or selection.
  function reset(): void {
    requestVersion++
    projects.value = []
    initialized.value = false
    loading.value = false
    error.value = null
    activeProjectId.value = null
    localStorage.removeItem(ACTIVE_PROJECT_STORAGE_KEY)
    searchQuery.value = ''
    hasMoreProjects.value = false
  }

  return {
    projects,
    initialized,
    loading,
    error,
    activeProjectId,
    activeProject,
    searchQuery,
    setSearchQuery,
    filteredProjects,
    hasMoreProjects,
    fetchProjects,
    selectProject,
    updateLinearAutoRun,
    reset,
  }
})
